import React from "react";
import styles from "./MultiHeaderReportTable.module.css";

/**
 * Multi-row header report table (no MUI).
 * columns:
 * - { field, label, width?, align? } OR
 * - { label, colSpan, children: [{ field, label, width?, align? }] }
 */
const flattenColumns = (columns) =>
  columns.flatMap((col) => (col?.children?.length ? col.children : [col]));

const MultiHeaderReportTable = ({
  columns = [],
  rows = [],
  title = null,
  subTitle = null,
  showSerialNumber = true,
  renderCell,
}) => {
  const leafColumns = flattenColumns(columns);
  const effectiveLeafColumns = showSerialNumber
    ? [{ field: "sno", label: "S.No.", width: "60px", align: "center" }, ...leafColumns]
    : leafColumns;

  const hasGroupedHeaders = columns.some((c) => Array.isArray(c?.children) && c.children.length);

  const getCellContent = (row, field, rowIndex) => {
    if (field === "sno") return `${rowIndex + 1}.`;
    if (renderCell) return renderCell(row, field, rowIndex);
    const v = row?.[field];
    return v === null || v === undefined || v === "" ? "-" : String(v);
  };

  return (
    <div className={styles.wrapper}>
      {title ? <div className={styles.title}>{title}</div> : null}
      {subTitle ? <div className={styles.subTitle}>{subTitle}</div> : null}

      <div className={styles.tableContainer}>
        <table className={styles.table}>
          <thead>
            {hasGroupedHeaders ? (
              <>
                <tr className={styles.headerRow}>
                  {showSerialNumber ? (
                    <th
                      className={`${styles.headerCell} ${styles.headerCellSno}`}
                      rowSpan={2}
                      style={{ width: "60px", textAlign: "center" }}
                    >
                      S.No.
                    </th>
                  ) : null}
                  {columns.map((col, idx) => {
                    const isGroup = Array.isArray(col?.children) && col.children.length;
                    return (
                      <th
                        key={idx}
                        className={styles.headerCell}
                        colSpan={isGroup ? col.children.length : 1}
                        rowSpan={isGroup ? 1 : 2}
                        style={{ width: col.width || "auto", textAlign: col.align || "left" }}
                      >
                        {col.label}
                      </th>
                    );
                  })}
                </tr>
                <tr className={styles.headerRow}>
                  {columns.flatMap((col, idx) => {
                    const isGroup = Array.isArray(col?.children) && col.children.length;
                    if (!isGroup) return [];
                    return col.children.map((child) => (
                      <th
                        key={`${idx}-${child.field}`}
                        className={styles.headerCell}
                        style={{
                          width: child.width || "auto",
                          textAlign: child.align || "left",
                        }}
                      >
                        {child.label}
                      </th>
                    ));
                  })}
                </tr>
              </>
            ) : (
              <tr className={styles.headerRow}>
                {effectiveLeafColumns.map((col) => (
                  <th
                    key={col.field}
                    className={`${styles.headerCell} ${
                      col.field === "sno" ? styles.headerCellSno : ""
                    }`}
                    style={{
                      width: col.width || "auto",
                      textAlign: col.align || (col.field === "sno" ? "center" : "left"),
                    }}
                  >
                    {col.label}
                  </th>
                ))}
              </tr>
            )}
          </thead>

          <tbody>
            {rows && rows.length ? (
              rows.map((row, rowIndex) => (
                <tr key={row?._id || rowIndex} className={styles.bodyRow}>
                  {effectiveLeafColumns.map((col) => (
                    <td
                      key={col.field}
                      className={`${styles.cell} ${col.field === "sno" ? styles.cellSno : ""}`}
                      style={{
                        textAlign: col.align || (col.field === "sno" ? "center" : "left"),
                        width: col.width || "auto",
                      }}
                    >
                      {getCellContent(row, col.field, rowIndex)}
                    </td>
                  ))}
                </tr>
              ))
            ) : (
              <tr>
                <td className={`${styles.emptyCell} noDataCell`} colSpan={effectiveLeafColumns.length}>
                  No information provided.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
};

export default MultiHeaderReportTable;

